import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { MANAGER_QUICK_ACTIONS, gateActions } from '@/components/dashboard/quickActions';
import { cn } from '@/lib/utils';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * A MANAGER'S QUICK ACTIONS — four tiles, one row
 *
 * This is a Manager's Content section, moved onto the page they land on. The
 * sidebar had these same four entries and now has none of them.
 *
 * ── Why the tile is stacked rather than a line ─────────────────────────────
 *
 * Icon, label and button in a row do not fit: a quarter of this column is
 * about 165px, and the icon, the label, the padding and a button want 180 —
 * so "Assessments" ran out of its box and over the plus. That is why the plus
 * was dropped once already.
 *
 * Stacked, the top line carries the icon and whatever the tile has to say
 * about itself — a plus, or a count of what is waiting — and the LABEL GETS
 * THE WHOLE WIDTH BELOW IT. Nothing can reach across it, at any width, for
 * any length of label.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function ManagerQuickActions({
  permissions,
  pending,
}: {
  permissions: string[];
  /** Live counts, by the name a tile asks for. Absent means no badge. */
  pending?: Partial<Record<'evaluations' | 'questionReports', number>>;
}) {
  const actions = gateActions(MANAGER_QUICK_ACTIONS, permissions);

  /* Every ordinary Employee lands here — they hold none of the four, and an
     empty card under a heading reads as something that failed to load. */
  if (actions.length === 0) return null;

  return (
    <Card className="overflow-hidden">
      <div className="flex items-baseline gap-2 border-b px-4 py-2.5">
        <h2 className="text-sm font-semibold">Quick actions</h2>
        <span className="hidden truncate text-xs text-muted-foreground sm:inline">
          What you build, and what is waiting on you
        </span>
      </div>

      <ul className="grid grid-cols-2 gap-2 p-2 lg:grid-cols-4">
        {actions.map((action) => {
          const Icon = action.icon;
          if (!action.view) return null;

          const waiting = action.pending ? (pending?.[action.pending] ?? 0) : 0;

          return (
            /*
              `relative`, and the plus is a SIBLING of the link rather than
              inside it — an anchor inside an anchor is invalid markup that
              browsers silently unnest.
            */
            <li key={action.label} className="relative">
              <Link
                to={action.view.to}
                title={action.hint}
                aria-label={
                  waiting > 0 ? `${action.view.label} — ${waiting} waiting` : action.view.label
                }
                className="flex h-full flex-col gap-1.5 rounded-lg border px-2.5 py-2.5 transition-colors hover:bg-muted/40"
              >
                <span className="flex items-center gap-2">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-brand-800 text-white">
                    <Icon className="h-3.5 w-3.5" aria-hidden />
                  </span>

                  {/*
                    The count sits on the icon's line, where there is nothing
                    to collide with. Shown only when there IS something
                    waiting: a permanent "0" on two of four tiles trains the
                    eye to skip the row, and the day it says 8 nobody looks.

                    `ml-auto` keeps it at the far end, and the plus below is
                    absolutely positioned in the same corner — the two never
                    appear together, because a queue has no create.
                  */}
                  {waiting > 0 && (
                    <span
                      className={cn(
                        'ml-auto inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1.5',
                        'text-[11px] font-semibold tabular-nums',
                        'bg-destructive/10 text-destructive',
                      )}
                    >
                      {waiting > 99 ? '99+' : waiting}
                    </span>
                  )}
                </span>

                {/*
                  Its own line, full width. Wraps rather than truncating — the
                  label is the whole of what the tile says, and half of it is
                  nothing.
                */}
                <span className="break-words text-[13px] font-medium leading-tight">
                  {action.label}
                </span>

                {/*
                  ── THE HINT, SURFACED ───────────────────────────────────────

                  It already existed on every action and was only a `title`
                  tooltip — so the one sentence saying what a tile is for was
                  visible to a mouse that hovered and to nobody else. "Question
                  Reports" tells you what the screen is called, not what it
                  holds.

                  Kept to one clamped line: this is a four-across row on a
                  dashboard, and a tile that grows to two or three lines of
                  explanation stops being a quick action. The `title` stays, so
                  a hint that does not fit is still reachable on hover.
                */}
                <span className="line-clamp-1 text-[11px] leading-tight text-muted-foreground">
                  {action.hint}
                </span>
              </Link>

              {action.create && (
                /* Top-right, level with the icon and clear of the label's line
                   below. Never on a tile that also carries a count. */
                <Link
                  to={action.create.to}
                  title={action.create.label}
                  aria-label={action.create.label}
                  className="absolute right-1.5 top-2.5 flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <Plus className="h-4 w-4" aria-hidden />
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
